import { FormControl, Grid, InputAdornment } from '@mui/material';
import { ErrorTypography, StyledOutlinedInput, StyledFormLabel } from 'components/atoms';
import {
  FieldValue,
  InputFieldProps,
  withGridItemCSS,
  withWrapperCSS,
} from 'components/organisms/forms/form-builder/types';
import { noop } from 'lodash';
import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import { IMaskInput } from 'react-imask';

interface TextMaskComponentProps {
  mask: string;
  name: string;
  onChange: (event: { target: { name: string; value: string } }) => void;
}

const TextMaskComponent = React.forwardRef<HTMLInputElement, TextMaskComponentProps>(
  function TextMaskCustom(props, ref) {
    const { onChange, mask, ...other } = props;
    const sanitizeValue = (value: string) => value.replace(/\D/g, '');

    return (
      <IMaskInput
        {...other}
        mask={mask || ''}
        definitions={{
          '#': /[1-9]/,
        }}
        inputRef={ref}
        onAccept={(value: any) => {
          const newValue = Array.isArray(value) ? value[0] : value;
          const sanitizedValue = sanitizeValue(String(newValue));
          onChange({ target: { name: props.name, value: sanitizedValue } });
        }}
        overwrite
      />
    );
  },
);

export const InputField: FC<
  InputFieldProps<FieldValue> & withGridItemCSS & withWrapperCSS
> = ({
  disabled = false,
  error,
  endAdornment,
  fieldKey,
  gridItemCss,
  initValue = '',
  label = '',
  labelCss,
  onChange = noop,
  onBlur = noop,
  hide = false,
  placeholder = '',
  required = false,
  type = 'text',
  wrapperCss,
  xs = 12,
  minValue = 0,
  multiline = false,
  rows = 1,
  restoreValueOnEmpty = true,
  maxValue,
  mask,
}) => {
  const [value, setValue] = useState(initValue);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (hide) {
      onChange(type === 'number' && !mask ? 0 : '', fieldKey);
      setValue(initValue);
    }
  }, [hide]);

  useEffect(() => {
    setValue(initValue);
  }, [initValue]);

  function onFieldChange({ target: { value: newValue } }: ChangeEvent<HTMLInputElement>) {
    onChange(type === 'number' && !mask ? Number(newValue) : newValue, fieldKey);
    setValue(newValue);
  }

  function handleFocus() {
    if (!disabled && !isFocused) {
      setIsFocused(true);
      if (type === 'number' && !mask && value === initValue) setValue('');
    }
  }

  function handleBlur(event: React.FocusEvent<HTMLInputElement>) {
    onBlur(event);
    setIsFocused(false);
    if (restoreValueOnEmpty && value === '') {
      setValue(initValue);
    }
  }

  return hide ? null : (
    <Grid item xs={xs} style={gridItemCss}>
      <FormControl
        error={!!error}
        required={required}
        style={{ width: '100%', ...wrapperCss }}
      >
        <StyledFormLabel error={!!error} style={labelCss}>
          {label} {required ? '*' : null}
        </StyledFormLabel>
        {mask ? (
          <StyledOutlinedInput
            id={fieldKey}
            value={value}
            onChange={onFieldChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            inputComponent={TextMaskComponent as any}
            inputProps={{ mask, 'data-testid': `field-${fieldKey}` }}
            endAdornment={<InputAdornment position="end">{endAdornment}</InputAdornment>}
          />
        ) : (
          <StyledOutlinedInput
            disabled={disabled}
            error={!!error}
            value={value}
            onChange={onFieldChange}
            placeholder={
              placeholder != '' ? placeholder.toString() : initValue.toString()
            }
            required={required}
            id={fieldKey}
            type={type}
            name={fieldKey}
            rows={multiline ? rows : 1}
            onFocus={handleFocus}
            onBlur={handleBlur}
            multiline={multiline}
            endAdornment={<InputAdornment position="end">{endAdornment}</InputAdornment>}
            inputProps={{
              max: maxValue,
              min: minValue,
              'data-testid': `field-${fieldKey}`,
            }}
          />
        )}
      </FormControl>
      <ErrorTypography>{error}</ErrorTypography>
    </Grid>
  );
};
